/**
 * AddContractualChargeModal Component
 *
 * Modal for adding or editing contractual charges.
 * Supports both percentage-based and flat fee charges.
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'components/Modal';
import FormInput from 'components/FormInput';
import Button from 'components/Button';
import './styles.scss';

// Helper function to format decimal values by removing trailing zeros
const formatDecimalValue = value => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  // Convert to number and back to string to remove trailing zeros
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return '';
  }
  // Convert to string, which will automatically remove trailing zeros
  return String(numValue);
};

const AddContractualChargeModal = ({
  show,
  onHide,
  onSave,
  charge,
  budgetId,
}) => {
  const [name, setName] = useState('');
  const [chargeType, setChargeType] = useState('percentage');
  const [percentage, setPercentage] = useState('');
  const [flatAmount, setFlatAmount] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (charge) {
      setName(charge.name || '');
      setChargeType(charge.charge_type || 'percentage');
      setPercentage(formatDecimalValue(charge.percentage));
      setFlatAmount(formatDecimalValue(charge.flat_amount));
    } else {
      // Reset form for new charge
      setName('');
      setChargeType('percentage');
      setPercentage('');
      setFlatAmount('');
    }
    // Reset saving state when modal opens/closes
    setIsSaving(false);
  }, [charge, show]);

  const handleSave = async () => {
    // Prevent double submission
    if (isSaving) {
      return;
    }

    // Validate form
    if (!name.trim()) {
      console.warn('Contractual charge name is required');
      return;
    }

    if (
      chargeType === 'percentage' &&
      (!percentage || isNaN(Number(percentage)))
    ) {
      console.warn('A valid contractual charge percentage is required');
      return;
    }

    if (
      chargeType === 'flat_fee' &&
      (!flatAmount || isNaN(Number(flatAmount)))
    ) {
      console.warn('A valid contractual charge flat amount is required');
      return;
    }

    if (!budgetId) {
      console.warn('A budget ID is required to add a contractual charge');
      return;
    }

    const payload = {
      budget: Number(budgetId),
      name: name.trim(),
      charge_type: chargeType,
      percentage: chargeType === 'percentage' ? Number(percentage) : null,
      flat_amount: chargeType === 'flat_fee' ? Number(flatAmount) : null,
    };

    if (charge?.id) {
      payload.id = charge.id;
    }

    setIsSaving(true);
    try {
      await onSave(payload);
      // Only close if save was successful
      handleClose();
    } catch (error) {
      // Error is handled in the parent component, but don't close modal on error
      setIsSaving(false);
      // Re-throw so parent can handle it
      throw error;
    }
  };

  const handleClose = () => {
    setName('');
    setChargeType('percentage');
    setPercentage('');
    setFlatAmount('');
    setIsSaving(false);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      title={charge ? 'Edit Contractual Charge' : 'Add Contractual Charge'}
      size="sm"
      className="add-contractual-charge-modal"
    >
      <div className="add-contractual-charge-modal__body">
        <div className="form-group">
          <label className="form-label">Title</label>
          <FormInput
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Agency Fee"
            autoFocus
          />
        </div>

        <div className="form-group">
          <label className="form-label">Type</label>
          <div className="type-selector">
            <button
              type="button"
              className={`type-button ${
                chargeType === 'percentage' ? 'active' : ''
              }`}
              onClick={() => {
                // When switching to percentage, preserve flatAmount value if percentage is empty
                if (!percentage && flatAmount) {
                  setPercentage(flatAmount);
                }
                setChargeType('percentage');
              }}
            >
              Percentage
            </button>
            <button
              type="button"
              className={`type-button ${
                chargeType === 'flat_fee' ? 'active' : ''
              }`}
              onClick={() => {
                // When switching to flat_fee, preserve percentage value if flatAmount is empty
                if (!flatAmount && percentage) {
                  setFlatAmount(percentage);
                }
                setChargeType('flat_fee');
              }}
            >
              Flat Fee
            </button>
          </div>
        </div>

        {chargeType === 'percentage' ? (
          <div className="form-group">
            <label className="form-label">Percent</label>
            <FormInput
              type="number"
              value={percentage}
              onChange={e => setPercentage(e.target.value)}
              placeholder="e.g., 20"
              min="0"
              max="100"
              step="0.01"
            />
          </div>
        ) : (
          <div className="form-group">
            <label className="form-label">Amount</label>
            <FormInput
              type="number"
              value={flatAmount}
              onChange={e => setFlatAmount(e.target.value)}
              placeholder="e.g., 10000"
              min="0"
              step="0.01"
            />
          </div>
        )}

        <div className="form-actions">
          <Button
            label="Cancel"
            variant="grey-outlined"
            onClick={handleClose}
          />
          <Button
            label="Done"
            variant="primary"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
            isDisabled={
              isSaving ||
              !name.trim() ||
              (chargeType === 'percentage' && !percentage) ||
              (chargeType === 'flat_fee' && !flatAmount)
            }
          />
        </div>
      </div>
    </Modal>
  );
};

AddContractualChargeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  charge: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    charge_type: PropTypes.string,
    percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    flat_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

AddContractualChargeModal.defaultProps = {
  charge: null,
};

export default AddContractualChargeModal;
